import actions from 'redux/actions'
import { getState } from 'redux/core'
import { constants, mixpanel, getPrefixedAddressFields, links } from 'common/helpers'
import { getFeatureToggle, getCookieValue } from 'helpers'

import {
  setupBegin, setupCommit, setupBeginPayPal, setupCommitPayPal, callVindicia, subscriptionProcessing,
  syncUserAndSubscription, setupBeginApplePay, setupCommitApplePay,
  callAnalytics,
} from './util'


const removeSubscriptionCase = () => {
  const { models: { payment: { subscriptionCase } } } = getState()

  if (subscriptionCase) {
    actions.models.payment.setSubscriptionCaseStatus(false)
  }
}

/**
 *
 * @param {object} values
 * @param {object} values.shipping
 * @param {object} values.billing
 * @param {string} values.paymentMethod
 * @param {string} values.plan
 * @param {boolean} values.proceedWithPrepaidCard
 * @returns {*}
 */
const begin = (values) => {
  // const error = checkRequiredFields(Object.keys(values), [
  //   'shippingFirstName',
  //   'shippingLastName',
  //   'shippingStreet',
  //   'shippingApt',
  //   'shippingZipCode',
  //   'shippingState',
  //   'shippingCity',
  //   'billingFirstName',
  //   'billingLastName',
  //   'billingStreet',
  //   'billingApt',
  //   'billingZipCode',
  //   'billingState',
  //   'billingCity',
  //   'plan',
  //   'paymentType',
  //   'token',
  //   'ignoreCredits',
  // ])
  //
  // if (error) {
  //   return Promise.reject(error)
  // }

  const {
    models: {
      requestTokens: { subscription: subscriptionToken },
      me: { needUpdateGender, gender },
    },
    view: {
      paymentPage: { useCredits },
    },
  } = getState()

  const { shipping, billing, paymentMethod, plan } = values

  const body = {
    ...getPrefixedAddressFields({ shipping, billing }),
    plan,
    paymentType: paymentMethod, // TODO ask backend rename `paymentType` to `paymentMethod`
    token: subscriptionToken,
    ignoreCredits: !useCredits,
  }

  if (values.proceedWithPrepaidCard) {
    body.proceedWithPrepaidCard = true
  }

  const begin = setupBegin({
    endpoint: 'rest/subscription/begin',
    tokenName: constants.requestTokens.subscription,
    withSubscriptionCase: true,
  })

  if (needUpdateGender) {
    return actions.models.me.update({ gender })
      .then(() => begin(body))
  }

  return begin(body)
}

const commit = (values) => {
  const { view: { paymentPage: { useCredits } } } = getState()

  const body = {
    ...values,
    ignoreCredits: !useCredits,
  }

  const commit = setupCommit({
    endpoint: 'rest/subscription/commit',
    tokenName: constants.requestTokens.subscription,
    withErrorModal: false,
    withProgressRequest: getFeatureToggle(constants.features.newSubscriptionPaymentProcess),
  })

  return commit(body)
}

/**
 * showBuySubscriptionErrorModal
 * SHow error if buy subscription is failed
 * @param {object} err
 * @param {object} values
 */
const showBuySubscriptionErrorModal = (err, values) => {
  const { title, message, resData: { authCode, isCvvError } } = err

  const isCardFailedPopupWithDiscountEnabled = getFeatureToggle(constants.features.cardFailedPopupWithDiscount)

  const isBillingError     = authCode === '713'
  const isPrepaidCardError = authCode === '309' || authCode === '110'

  actions.view.paymentPage.setBillingError(isBillingError)

  if (isCvvError) {
    actions.models.modals.open(constants.modals.ErrorCVV, {
      onSubmit: ({ cvv }) => buySubscription({ ...values, creditCard: { ...values.creditCard, cvv } }), // TODO fix it on old app
    })
  }
  else if (isPrepaidCardError) {
    const withDiscountModalVisible = authCode === '309' && isCardFailedPopupWithDiscountEnabled

    const withSubscriptionPlans = (
      !getCookieValue(constants.cookieNames.isPrepaidCardWithSubscriptionPlansModalHidden)
      && authCode === '309'
    )

    if (withDiscountModalVisible) {
      actions.models.modals.open(constants.modals.prepaidCard.PrepaidCardWithDiscount, {
        authCode,
        handleProceed: () => buySubscription({ ...values, proceedWithPrepaidCard: true }),
      })
    }
    else if (withSubscriptionPlans) {
      actions.models.modals.open(constants.modals.prepaidCard.PrepaidCardWithSubscriptionPlans, {
        onChoose: (plan) => {
          actions.view.paymentPage.setSelectedPlan(plan)
          buySubscription({ ...values, plan })
        },
      })
    }
    else {
      actions.models.modals.open(constants.modals.ServerError, { title, content: message })
    }
  }
  else {
    actions.models.modals.open(constants.modals.ServerError, { title, content: message })
  }
}

/**
 *
 * @param {object} values
 * @param {object} values.shipping
 * @param {object} values.billing
 * @param {object} values.creditCard
 * @param {string} values.paymentType
 * @param {string} values.paymentMethod
 * @param {string} values.plan
 * @param {boolean} values.proceedWithPrepaidCard
 */
const buySubscription = (values) => {
  const { shipping, billing, creditCard, paymentType, isEnoughCreditsToBuy } = values

  mixpanel.track(constants.analyticsEvents.tech.buySubscription)

  actions.models.payment.update({ isSubscriptionFetching: true })
  actions.models.payment.setUserShippingAddress(values.shipping)

  return begin(values)
    .then(
      (data) => {
        const body = {
          paymentType,
          shipping,
          billing,
          creditCard,
          ...data,
        }

        if (isEnoughCreditsToBuy) {
          return Promise.resolve(data)
        }

        return callVindicia(body)
      },
      (err) => Promise.reject(err)
    )
    .then(
      ({ subscriptionSession, token }) => commit({ subscriptionSession, token }),
      (err) => Promise.reject(err)
    )
    .then(
      (result) => {
        syncUserAndSubscription({ paymentType, ...result })

        mixpanel.track(constants.analyticsEvents.paymentForm.placeOrder, {
          success: true,
        })
      },
      (err) => {
        const { title, message, resData: { authCode } = {} } = err

        showBuySubscriptionErrorModal(err, values)
        mixpanel.track(constants.analyticsEvents.paymentForm.placeOrder, {
          success: false,
          reason: `${authCode} | ${title} | ${message}`,
        })

        return Promise.reject(err)
      }
    )
    .finally(() => {
      actions.models.payment.update({ isSubscriptionFetching: false })
    })
    .then(() => {
      const { view: { postponedPremiumProduct: { product } } } = getState()

      clearResolvedProducts()
      actions.view.paymentPage.setBillingError(false)
      actions.misc.cookies.removeItem(constants.cookieNames.selectedPlan)

      if (product) {
        actions.models.queue.addItem(product, { placement: 'buy subscription premium product' })
      }

      callAnalytics()
      actions.models.router.push(links.thankYou)
      removeSubscriptionCase()
    })
}


// PayPal

const beginSubscriptionUsingPayPal = (values) => {
  const beginPayPal = setupBeginPayPal({
    begin,
    sessionName: 'subscriptionSession',
  })

  actions.models.payment.setUserShippingAddress(values.shipping)

  return beginPayPal(values)
}

/**
 *
 * @param {object} values
 * @param {string} values.paymentType
 * @param {string} values.paypalSessionId
 */
const commitSubscriptionUsingPayPal = (values) => {
  const commitPayPal = setupCommitPayPal({
    commit,
    sessionName: 'subscriptionSession',
  })

  return commitPayPal(values)
    .then((data) => {
      clearResolvedProducts()
      removeSubscriptionCase()

      return data
    })
}


// ApplePay

const beginSubscriptionUsingApplePay = (values) => {
  const { models: { me: { needUpdateGender, gender } } } = getState()

  const begin = setupBegin({
    endpoint: 'rest/subscription/begin',
    tokenName: constants.requestTokens.subscription,
    withSubscriptionCase: true,
  })

  const beginApplePay = setupBeginApplePay({
    begin,
    tokenName: constants.requestTokens.subscription,
    sessionName: 'subscriptionSession',
  })

  actions.models.payment.setUserShippingAddress(values.shipping)

  if (needUpdateGender) {
    return actions.models.me.update({ gender })
      .then(() => beginApplePay({ ...values }))
  }

  return beginApplePay({ ...values })
}

const commitSubscriptionUsingApplePay = (session, values) => {
  const commitApplePay = setupCommitApplePay({
    commit,
    session,
    sessionName: 'subscriptionSession',
    paymentType: constants.paymentTypes.subscription,
    tokenName: constants.requestTokens.subscription,
  })

  return commitApplePay(values)
    .then(
      () => {
        clearResolvedProducts()
        callAnalytics()
        actions.models.router.push(links.thankYou)
        removeSubscriptionCase()
      },
      (err) => Promise.reject(err)
    )
}


const postponedSubscriptionProcessing = () => {
  const { models: { payment: { subscriptionProgress } } } = getState()

  if (subscriptionProgress) {
    const { status, subscriptionSessionId } = subscriptionProgress

    if (!subscriptionSessionId || status !== constants.subscriptionProcessingStatuses.progress) {
      return
    }

    mixpanel.track(constants.analyticsEvents.tech.postponedSubscriptionProcessing)

    actions.models.payment.update({
      isSubscriptionFetching: true,
      instantShowDisplayProcessBanner: true,
    })

    subscriptionProcessing({ subscriptionSession: subscriptionSessionId })
      .then(
        (result) => {
          syncUserAndSubscription({
            paymentType: constants.paymentTypes.subscription,
            ...result,
          })

          clearResolvedProducts()
          actions.misc.cookies.removeItem(constants.cookieNames.selectedPlan)

          callAnalytics()
          actions.models.router.push(links.thankYou)
        },
        ({ title, message }) => {
          actions.models.modals.open(constants.modals.ServerError, { title, content: message })
        }
      )
      .finally(() => {
        actions.models.payment.update({
          isSubscriptionFetching: false,
          instantShowDisplayProcessBanner: false,
        })
      })
  }
}

const clearResolvedProducts = () => {
  const { view: { pickPage: { selectedItems } } } = getState()

  const isPremiumProductsForNotSubscribedEnabled = getFeatureToggle(constants.features.premiumProductsForNotSubscribed)

  if (isPremiumProductsForNotSubscribedEnabled && selectedItems.length) {
    actions.view.pickPage.clearSelectedItems()
  }

  actions.models.cart.clear()
}


export {
  buySubscription,
  begin,
  commit,
  beginSubscriptionUsingPayPal,
  commitSubscriptionUsingPayPal,
  beginSubscriptionUsingApplePay,
  commitSubscriptionUsingApplePay,
  postponedSubscriptionProcessing,
}
